// Transforms raw report payload into chart-ready structures via a large
// useEffect. Dep array is preserved from the original: selectedQuestionId
// and setSelectedQuestionId are intentionally kept OUT of deps to avoid
// re-running the transformation on every selection change (stale-closure
// by design). Optional chaining on survey?.end_date tolerates null during
// loading without throwing.

import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

import { calcMedian } from "../data-transforms";

export function useReportsDerivedData({
    rawSurveyReport,
    survey,
    surveyQuestions,
    surveyAbuse,
    selectedQuestionId,
    setSelectedQuestionId,
}) {
    // State declarations (lines 89-103 of reports-tab.js before extraction).
    const [pieDemographicData, setPieDemographicData] = useState([]);
    const [completionData, setCompletionData] = useState();
    const [surveyTimePeriod, setSurveyTimePeriod] = useState({});
    const [questionDetails, setQuestionDetails] = useState([]);
    const [totalResponses, setTotalResponses] = useState(0);
    const [totalUnThemed, setTotalUnThemed] = useState(0);
    const [totalVotes, setTotalVotes] = useState(0);
    const [totalModeratedResponses, setTotalModeratedResponses] = useState(0);
    const [topThemesForSurvey, setTopThemesForSurvey] = useState([]);
    const [allResponsesByThemePerQ, setAllResponsesByThemePerQ] = useState([]);
    const [topThemesForEachQuestion, setTopThemesForEachQuestion] = useState([]);
    const [questionEngagementData, setQuestionEngagementData] = useState([]);
    const [themeEngagementData, setThemeEngagementData] = useState([]);
    const [perQuestionData, setPerQuestionData] = useState([]);

    // Top 10 reversed for Nivo horizontal charts (first item = bottom).
    const top10Reversed = (arr) => [...arr].slice(0, 10).reverse();

    // Chart-ready variants of themeEngagementData (lines 109-133).
    const themeByContributors = useMemo(() =>
        [...themeEngagementData].sort((a, b) => Number(b.contributors) - Number(a.contributors)),
    [themeEngagementData]
    );
    const themeByContributorsForChart = useMemo(() => top10Reversed(themeByContributors), [themeByContributors]);
    const themeByResponses = useMemo(() =>
        [...themeEngagementData].sort((a, b) => Number(b.responses) - Number(a.responses)),
    [themeEngagementData]
    );
    const themeByResponsesForChart = useMemo(() => top10Reversed(themeByResponses), [themeByResponses]);
    const themeByUnique = useMemo(() =>
        [...themeEngagementData].sort((a, b) => Number(b.uniqueResponses) - Number(a.uniqueResponses)),
    [themeEngagementData]
    );
    const themeByUniqueForChart = useMemo(() => top10Reversed(themeByUnique), [themeByUnique]);
    // For grouped/stacked theme charts: sort by total of all metrics
    const themeByTotalEngagement = useMemo(() =>
        [...themeEngagementData].sort((a, b) =>
            (Number(b.contributors) + Number(b.responses) + Number(b.uniqueResponses)) -
            (Number(a.contributors) + Number(a.responses) + Number(a.uniqueResponses))
        ),
    [themeEngagementData]
    );
    const themeByTotalEngagementForChart = useMemo(() => top10Reversed(themeByTotalEngagement), [themeByTotalEngagement]);

    // Chart-ready variants of questionEngagementData (lines 135-162).
    const questionByContributors = useMemo(() =>
        [...questionEngagementData].sort((a, b) => Number(b.contributors) - Number(a.contributors)),
    [questionEngagementData]
    );
    const questionByContributorsForChart = useMemo(() => top10Reversed(questionByContributors), [questionByContributors]);
    const questionByResponses = useMemo(() =>
        [...questionEngagementData].sort((a, b) => Number(b.responses) - Number(a.responses)),
    [questionEngagementData]
    );
    const questionByResponsesForChart = useMemo(() => top10Reversed(questionByResponses), [questionByResponses]);
    const questionByUnique = useMemo(() =>
        [...questionEngagementData].sort((a, b) => Number(b.uniqueResponses) - Number(a.uniqueResponses)),
    [questionEngagementData]
    );
    const questionByUniqueForChart = useMemo(() => top10Reversed(questionByUnique), [questionByUnique]);
    // For grouped/stacked question charts: sort by total of all metrics
    const questionByTotalEngagement = useMemo(() => {
        const withTotals = questionEngagementData.map(q => ({
            ...q,
            _total: Number(q.contributors || 0) + Number(q.responses || 0) + Number(q.uniqueResponses || 0)
        }));
        const sorted = withTotals.sort((a, b) => b._total - a._total);
        return sorted;
    },
    [questionEngagementData]
    );
    const questionByTotalEngagementForChart = useMemo(() => top10Reversed(questionByTotalEngagement), [questionByTotalEngagement]);

    // The giant transformation effect. Body preserved verbatim from
    // reports-tab.js lines 164-879.
    useEffect(() => {
        const rawData = structuredClone(rawSurveyReport);
        if (surveyAbuse?.abuse_reports) {
            const totalModeratedResponsesCount = surveyAbuse.abuse_reports.filter(
                (report) =>
                    report.status === "accepted" || report.status === "rejected",
            ).length;
            setTotalModeratedResponses(totalModeratedResponsesCount);
        }
        if (rawData?.report?.[0]?.[0] && surveyQuestions) {
            // Responses per Q - rawData.report is nested array [[...]]
            // Sort inner array before flattening to ensure correct order
            let sortedInnerArray = rawData.report[0].sort(
                (a, b) => a.question_position - b.question_position,
            );
            let surveyReport = [sortedInnerArray];
            let surveyReportProcessed = surveyReport.flat();
            surveyReportProcessed = surveyReportProcessed.map((q) => {
                const question = structuredClone(q);
                const responses = question.responses;
                let processedResponses = [];
                responses.forEach((answer) => {
                    const responseArrayIndex = processedResponses.findIndex(
                        (r) => r?.Response_ID === answer.Response_ID,
                    );
                    if (responseArrayIndex > -1) {
                        processedResponses[responseArrayIndex].themes.push({
                            theme_id: answer.Response_Theme_ID,
                            theme_name: answer.Response_Theme,
                        });
                    } else {
                        const newIndex = processedResponses.push({
                            Respondent_UID_Survey_Submission_Timestamp:
                                answer.Respondent_UID_Survey_Submission_Timestamp,
                            Response_Text: answer.Response_Text,
                            Respondent_UID_Creation_Timestamp:
                                answer.Respondent_UID_Creation_Timestamp,
                            Response_Timestamp: answer.Response_Timestamp,
                            Response_Type: answer.Response_Type,
                            Is_Moderated: answer.Is_Moderated,
                            Response_ID: answer.Response_ID,
                            Respondent_UID: answer.Respondent_UID,
                            Response_UID: answer.Response_UID,
                            Response_Times_Chosen: answer.Response_Times_Chosen,
                            Response_Is_Matched: answer.Response_Is_Matched,
                            themes: [],
                        });
                        if (answer.Response_Theme_ID) {
                            processedResponses[newIndex - 1].themes = [
                                {
                                    theme_id: answer.Response_Theme_ID,
                                    theme_name: answer.Response_Theme,
                                },
                            ];
                        }
                    }
                });
                question.responses = processedResponses;
                return question;
            });

            // survey completion data
            // Respondents & their responses by Q
            let allResponsesOnly = surveyReportProcessed
                .map((q) => {
                    return q.responses;
                })
                .flat();
            let allRespondentsData = {
                respondents: [],
                total_respondents: 0,
                respondents_finished: 0,
            };
            allResponsesOnly.forEach((answer) => {
                const respondentId = answer.Respondent_UID;
                const respondentArrayIndex =
                    allRespondentsData.respondents.findIndex(
                        (t) => t?.respondent_id === respondentId,
                    );
                if (respondentArrayIndex > -1) {
                    allRespondentsData.respondents[
                        respondentArrayIndex
                    ].responses.push({
                        response_id: answer.Response_ID,
                        response_text: answer.Response_Text,
                    });
                    allRespondentsData.respondents[respondentArrayIndex]
                        .responses_count++;
                } else {
                    allRespondentsData.respondents.push({
                        respondent_id: respondentId,
                        survey_start_time:
                            answer.Respondent_UID_Creation_Timestamp,
                        survey_completion_time:
                            answer.Respondent_UID_Survey_Submission_Timestamp,
                        responses_count: 1,
                        responses: [
                            {
                                response_id: answer.Response_ID,
                                response_text: answer.Response_Text,
                            },
                        ],
                    });
                    allRespondentsData.total_respondents++;
                    if (answer.Respondent_UID_Survey_Submission_Timestamp) {
                        allRespondentsData.respondents_finished++;
                    }
                }
            });

            // Set Completion Pie Chart
            if (allRespondentsData.respondents.length) {
                setCompletionData(allRespondentsData);
                let surveyCompletionTimeAverageFinished = [];
                let surveyCompletionTimeAverageNotFinished = [];
                allRespondentsData.respondents.forEach((respondent) => {
                    if (respondent?.survey_completion_time) {
                        surveyCompletionTimeAverageFinished.push(
                            dayjs
                                .duration(
                                    dayjs(
                                        respondent.survey_completion_time,
                                        "YYYY-MM-DD HH:mm:ss",
                                    ).diff(
                                        dayjs(
                                            respondent.survey_start_time,
                                            "YYYY-MM-DD HH:mm:ss",
                                        ),
                                    ),
                                )
                                .asMilliseconds(),
                        );
                    } else {
                        const surveyLeft =
                            survey.state === "finished"
                                ? dayjs(survey.end_date)?.format(
                                    "YYYY-MM-DDTHH:mm",
                                )
                                : dayjs();
                        surveyCompletionTimeAverageNotFinished.push(
                            dayjs
                                .duration(
                                    dayjs(surveyLeft).diff(
                                        dayjs(
                                            respondent.survey_start_time,
                                            "YYYY-MM-DD HH:mm:ss",
                                        ),
                                    ),
                                )
                                .asMilliseconds(),
                        );
                    }
                });

                // Calculate Survey Time Period data
                const startTimes = allRespondentsData.respondents.map(r =>
                    dayjs(r.survey_start_time, "YYYY-MM-DD HH:mm:ss")
                ).filter(d => d.isValid());
                const endTimes = allRespondentsData.respondents
                    .filter(r => r.survey_completion_time)
                    .map(r => dayjs(r.survey_completion_time, "YYYY-MM-DD HH:mm:ss"))
                    .filter(d => d.isValid());

                const earliestStart = startTimes.length ? startTimes.reduce((min, d) => d.isBefore(min) ? d : min) : null;
                const latestEnd = endTimes.length ? endTimes.reduce((max, d) => d.isAfter(max) ? d : max) : null;
                const trimmedCompletionTimes = surveyCompletionTimeAverageFinished
                    .sort((a, b) => a - b)
                    .slice(Math.floor(surveyCompletionTimeAverageFinished.length * 0.1),
                        Math.ceil(surveyCompletionTimeAverageFinished.length * 0.9));

                setSurveyTimePeriod({
                    started: earliestStart ? earliestStart.format("DD-MM-YYYY HH:mm:ss") : "N/A",
                    finished: latestEnd ? latestEnd.format("DD-MM-YYYY HH:mm:ss") : "N/A",
                    avgCompletionTime: trimmedCompletionTimes.length ?
                        calcMedian(trimmedCompletionTimes) : 0,
                });
            }

            // Respondents & their responses by Q
            const allRespondentsByQ = surveyReportProcessed.map((q) => {
                const responses = q?.responses;
                let respondentsArray = [];
                responses.forEach((answer) => {
                    const respondentId = answer.Respondent_UID;
                    const respondentArrayIndex = respondentsArray.findIndex(
                        (t) => t?.respondent_id === respondentId,
                    );
                    if (respondentArrayIndex > -1) {
                        respondentsArray[respondentArrayIndex].responses.push({
                            response_id: answer.Response_ID,
                        });
                        respondentsArray[respondentArrayIndex]
                            .responses_count++;
                    } else {
                        respondentsArray.push({
                            respondent_id: respondentId,
                            survey_start_time:
                                answer.Respondent_UID_Creation_Timestamp,
                            survey_completion_time:
                                answer.Respondent_UID_Survey_Submission_Timestamp,
                            responses_count: 1,
                            responses: [
                                {
                                    response_id: answer.Response_ID,
                                },
                            ],
                        });
                    }
                });
                return respondentsArray;
            });
            let respondentResponseAmount = [];
            allRespondentsByQ.flat().forEach((questionResponses) => {
                let respondenseAmountIndex = respondentResponseAmount.findIndex(
                    (t) =>
                        t?.responses_count ===
                        questionResponses.responses_count,
                );
                if (respondenseAmountIndex > -1) {
                    respondentResponseAmount[respondenseAmountIndex]
                        .respondents++;
                } else {
                    respondentResponseAmount.push({
                        responses_count: questionResponses.responses_count,
                        respondents: 1,
                        label: `Respondents who gave ${questionResponses.responses_count} Responses`,
                    });
                }
            });
            // Demographics Data
            const demographicQuestions = surveyReportProcessed.filter((q) =>
                Boolean(q.Is_Attribute_Question),
            );
            const demographicQuestionData = demographicQuestions.map(
                (question) => {
                    let clonedQuestion = structuredClone(question);
                    let pieQuestionData = [];
                    clonedQuestion?.responses.forEach((response) => {
                        const answerId = response.Response_UID;
                        const responseIDArrayIndex = pieQuestionData.findIndex(
                            (r) => r?.answer_id === answerId,
                        );
                        const surveyCompleted = Boolean(
                            response.Respondent_UID_Survey_Submission_Timestamp,
                        );
                        if (responseIDArrayIndex > -1) {
                            pieQuestionData[responseIDArrayIndex].value++;
                            if (surveyCompleted) {
                                pieQuestionData[responseIDArrayIndex]
                                    .completed++;
                            }
                        } else {
                            pieQuestionData.push({
                                answer_id: answerId,
                                completed: surveyCompleted ? 1 : 0,
                                id: response.Response_Text,
                                label: response.Response_Text,
                                value: 1,
                            });
                        }
                    });
                    clonedQuestion.pieData = pieQuestionData;
                    return clonedQuestion;
                },
            );
            if (demographicQuestionData) {
                setPieDemographicData(demographicQuestionData);
            }

            // Responses by theme per Q
            let themeAnswersKeys = [];
            let themeResponseArray = [];
            const responsesByThemeMap = surveyReportProcessed.map((q) => {
                const responses = q?.responses;
                const surveyQuestion = surveyQuestions?.find(
                    (sq) => sq.position === q.Question_Position,
                );
                const presetAnswers = surveyQuestion?.preset_answers || [];
                let questionThemeResponsesArray = [];
                let unThemedCount = 0;
                const seenQUnthemedUIDs = new Set();
                responses.forEach((response) => {
                    // Identify if the response is a predefined preset answer.
                    // Comparison is text-based to reliably distinguish designer presets from organic respondent feedback.
                    const isPreset = presetAnswers.some((pa) => {
                        const paText = typeof pa === "string" ? pa : pa?.text;
                        return (
                            paText?.trim().toLowerCase() ===
                            response.Response_Text?.trim().toLowerCase()
                        );
                    });
                    if (response?.themes?.length) {
                        response.themes.forEach((theme) => {
                            const responseThemeIndex =
                                themeResponseArray.findIndex(
                                    (t) => t?.id === theme.theme_id,
                                );
                            const questionResponseThemeIndex =
                                questionThemeResponsesArray.findIndex(
                                    (t) => t?.theme_id === theme.theme_id,
                                );
                            if (responseThemeIndex > -1) {
                                themeResponseArray[responseThemeIndex]
                                    .responses_count++;
                            } else {
                                themeResponseArray.push({
                                    id: theme.theme_id,
                                    name: theme.theme_name,
                                    responses_count: 1,
                                });
                            }
                            if (questionResponseThemeIndex > -1) {
                                questionThemeResponsesArray[
                                    questionResponseThemeIndex
                                ].responses_count++;
                            } else {
                                questionThemeResponsesArray.push({
                                    theme_id: theme.theme_id,
                                    theme_name: theme.theme_name,
                                    responses_count: 1,
                                });
                            }
                            const themeKey = themeAnswersKeys.find(
                                (t) => t === theme.theme_name,
                            );
                            if (!themeKey) {
                                themeAnswersKeys.push(theme.theme_name);
                            }
                        });
                    } else if (!isPreset && surveyQuestion?.question_type === 'freeform') {
                        if (!seenQUnthemedUIDs.has(response.Response_UID)) {
                            seenQUnthemedUIDs.add(response.Response_UID);
                            unThemedCount++;
                        }
                    }
                });
                let questionThemes = {
                    question_name: q.Question,
                    question_position: q.Question_Position,
                    themes: questionThemeResponsesArray,
                    theme_count: 0,
                    unThemed: unThemedCount,
                };
                questionThemeResponsesArray.forEach((theme) => {
                    const theme_name = theme.theme_name;
                    if (questionThemes[theme_name]) {
                        questionThemes[theme_name] =
                            questionThemes[theme_name] + theme.responses_count;
                    } else {
                        questionThemes[theme_name] = theme.responses_count;
                    }
                });
                return questionThemes;
            });

            // Calculate top 3 themes for survey from themeResponseArray
            let topThemes = themeResponseArray && themeResponseArray.length > 0
                ? [...themeResponseArray]
                    .sort((a, b) => b.responses_count - a.responses_count)
                    .slice(0, 3)
                    .map((theme, index) => ({
                        id: theme.id,
                        name: theme.name,
                        responses_count: theme.responses_count,
                        rank: index + 1,
                    }))
                : [];
            // Calculate total responses and unthemed
            let totalResponsesCount = 0;
            let totalUnThemedCount = 0;
            let totalVotesCount = 0;

            if (surveyReportProcessed) {
                const uniqueAnswers = {};
                const seenUnthemedUIDs = new Set();
                totalResponsesCount = surveyReportProcessed.reduce(
                    (acc, q) => acc + (q.responses ? q.responses.length : 0),
                    0,
                );
                surveyReportProcessed.forEach((q) => {
                    const surveyQuestion = surveyQuestions?.find(
                        (sq) => sq.position === q.Question_Position,
                    );
                    // Skip Mixed questions - matching is disabled for these (#1004)
                    if (surveyQuestion?.question_type === 'mixed') return;

                    q.responses.forEach((r) => {
                        // Count un-themed responses for freeform/Gobby questions only (deduplicated by Response_UID)
                        if (!r.themes?.length && surveyQuestion?.question_type === 'freeform') {
                            if (!seenUnthemedUIDs.has(r.Response_UID)) {
                                seenUnthemedUIDs.add(r.Response_UID);
                                totalUnThemedCount++;
                            }
                        }
                        if (r.Response_Is_Matched && !uniqueAnswers[r.Response_UID]) {
                            uniqueAnswers[r.Response_UID] =
                                Math.max(0, (Number(r.Response_Times_Chosen) || 0) - 1);
                        }
                    });
                });
                totalVotesCount = Object.values(uniqueAnswers).reduce(
                    (a, b) => a + b,
                    0,
                );
            }

            setTotalResponses(totalResponsesCount);
            setTotalUnThemed(totalUnThemedCount);
            setTotalVotes(totalVotesCount);
            setAllResponsesByThemePerQ(responsesByThemeMap);
            setTopThemesForSurvey(topThemes);

            // Calculate top themes for each question
            const topThemesForEachQuestionData = responsesByThemeMap.map((question) => {
                const sortedThemes = question.themes
                    .sort((a, b) => b.responses_count - a.responses_count);
                const topThemesForQuestion = sortedThemes.slice(0, 3).map((theme, index) => ({
                    rank: index + 1,
                    name: theme.theme_name,
                }));
                return {
                    questionNumber: question.question_position,
                    questionText: question.question_name,
                    topThemes: topThemesForQuestion,
                    reasonIfNotThemed: question.themes.length === 0 ? "No themes applied" : "",
                };
            });
            setTopThemesForEachQuestion(topThemesForEachQuestionData);

            // Prepare question engagement data
            const questionEngagement = surveyReportProcessed
                .map((question) => {
                    const surveyQuestion = surveyQuestions.find(q => q.position === question.Question_Position);
                    const uniqueResponses = new Set(question.responses.map(r => r.Response_UID)).size;
                    const contributors = new Set(question.responses.map(r => r.Respondent_UID)).size;
                    const responses = question.responses.length;
                    return {
                        number: question.Question_Position,
                        question: question.Question,
                        contributors,
                        responses,
                        uniqueResponses,
                        containsPii: surveyQuestion?.contains_pii,
                    };
                })
                .filter(q => !q.containsPii)
                .sort((a, b) => b.responses - a.responses);
            setQuestionEngagementData(questionEngagement);

            // Prepare theme engagement data
            const themeEngagementMap = {};
            surveyReportProcessed.forEach((question) => {
                const surveyQuestion = surveyQuestions.find(q => q.position === question.Question_Position);
                // Skip Mixed questions - theming charts removed for these (#990)
                if (surveyQuestion?.question_type === 'mixed') return;
                question.responses.forEach((response) => {
                    if (response.themes.length > 0) {
                        response.themes.forEach((theme) => {
                            if (!themeEngagementMap[theme.theme_name]) {
                                themeEngagementMap[theme.theme_name] = {
                                    contributors: new Set(),
                                    responses: 0,
                                    uniqueResponses: new Set(),
                                };
                            }
                            themeEngagementMap[theme.theme_name].contributors.add(response.Respondent_UID);
                            themeEngagementMap[theme.theme_name].responses++;
                            themeEngagementMap[theme.theme_name].uniqueResponses.add(response.Response_UID);
                        });
                    }
                });
            });
            const themeEngagement = Object.entries(themeEngagementMap).map(([theme, data]) => ({
                theme,
                contributors: data.contributors.size,
                responses: data.responses,
                uniqueResponses: data.uniqueResponses.size,
            })).sort((a, b) => b.responses - a.responses);
            setThemeEngagementData(themeEngagement);

            if (
                surveyReportProcessed.length &&
                allRespondentsData &&
                respondentResponseAmount
            ) {
                let mostPopularQuestion = [...surveyReportProcessed]
                    .sort((a, b) => b.responses.length - a.responses.length)
                    .shift();
                if (mostPopularQuestion) {
                    // Build Question Details data
                    const questionDetailsData = surveyReportProcessed.map((question) => {
                        const surveyQuestion = surveyQuestions.find(q => q.position === question.Question_Position);
                        const questionType = surveyQuestion?.question_type || 'fixed';
                        let displayType = 'Preset';
                        if (questionType === 'freeform') {
                            displayType = 'Gobby';
                        } else if (questionType === 'mixed') {
                            displayType = 'Mixed';
                        }

                        const numPresets = ((displayType === 'Preset' || displayType === 'Mixed') ? surveyQuestion?.preset_answers?.length : 'N/A');
                        const responseType = surveyQuestion?.answer_type === 'multiple' ? 'Multi' : 'Single';
                        const demographic = surveyQuestion?.is_demographic_question;
                        const pii = surveyQuestion?.contains_pii;

                        return {
                            number: question.Question_Position,
                            text: question.Question,
                            type: displayType,
                            numPresets: numPresets,
                            responseType: responseType,
                            demographic: demographic,
                            pii: pii,
                        };
                    });
                    setQuestionDetails(questionDetailsData);

                    // Process per question data
                    const perQuestionDataArray = surveyReportProcessed.map((question) => {
                        const questionDetail = questionDetailsData.find(q => q.number === question.Question_Position);
                        const themesData = responsesByThemeMap.find(q => q.question_position === question.Question_Position) || {};

                        const responses = question.responses.length;
                        const uniqueResponses = new Set(question.responses.map(r => r.Response_UID)).size;
                        const contributors = new Set(question.responses.map(r => r.Respondent_UID)).size;

                        const uniqueAnswers = {};
                        question.responses.forEach((r) => {
                            if (r.Response_Is_Matched && !uniqueAnswers[r.Response_UID]) {
                                uniqueAnswers[r.Response_UID] = {
                                    text: r.Response_Text,
                                    votes: Math.max(0, (r.Response_Times_Chosen || 0) - 1),
                                };
                            }
                        });

                        const votes = Object.values(uniqueAnswers).reduce((acc, answer) => acc + answer.votes, 0);
                        const moderated = question.responses.filter(r => r.Is_Moderated).length;
                        const unthemed = themesData.unThemed || 0;

                        // Most Matched Responses: top 3 by Response_Times_Chosen
                        const mostVoted = Object.values(uniqueAnswers)
                            .sort((a, b) => b.votes - a.votes)
                            .slice(0, 3);

                        // Enhanced data for charts
                        const surveyQuestion = surveyQuestions.find(q => q.position === question.Question_Position);
                        const presetAnswers = surveyQuestion?.preset_answers || [];
                        const displayType = questionDetail.type;

                        // presetData
                        const presetData = presetAnswers
                            .map((opt) => {
                                const optId =
                                    typeof opt === "string" ? opt : opt?.nanoid;
                                const responseForOption =
                                    question.responses.find(
                                        (r) => r.Response_UID === optId,
                                    );
                                return {
                                    id: opt,
                                    responses: responseForOption
                                        ? Number(
                                            responseForOption.Response_Times_Chosen,
                                        ) || 0
                                        : 0,
                                };
                            })
                            .sort((a, b) => b.responses - a.responses);

                        // themesEngagement
                        const gobbyResponses = question.responses.filter(
                            (r) =>
                                !presetAnswers.some((pa) => {
                                    const paText =
                                        typeof pa === "string" ? pa : pa?.text;
                                    return (
                                        paText?.trim().toLowerCase() ===
                                        r.Response_Text?.trim().toLowerCase()
                                    );
                                }),
                        );
                        // Only count un-themed for freeform/Gobby questions (not Mixed - matching disabled #1004)
                        const isFreeformQuestion = displayType === 'Gobby';
                        const unthemedCount = isFreeformQuestion
                            ? new Set(gobbyResponses.filter(r => !r.themes?.length).map(r => r.Response_UID)).size
                            : 0;
                        const themeMap = {};
                        gobbyResponses.forEach(r => {
                            if (r.themes?.length) {
                                r.themes.forEach(theme => {
                                    const name = theme.theme_name;
                                    if (!themeMap[name]) themeMap[name] = { contributors: new Set(), responses: 0, uniqueResponses: new Set(), votes: 0 };
                                    themeMap[name].contributors.add(r.Respondent_UID);
                                    themeMap[name].responses++;
                                    themeMap[name].uniqueResponses.add(r.Response_UID);
                                    themeMap[name].votes += Math.max(0, (r.Response_Times_Chosen || 0) - 1);
                                });
                            }
                        });
                        const themesEngagement = Object.entries(themeMap).map(([id, data]) => ({
                            id,
                            contributors: data.contributors.size,
                            responses: data.responses,
                            uniqueResponses: data.uniqueResponses.size,
                            votes: data.votes
                        })).concat(unthemedCount > 0 ? [{id: 'Un-themed', contributors: new Set(gobbyResponses.filter(r => !r.themes?.length).map(r => r.Respondent_UID)).size, responses: unthemedCount, uniqueResponses: new Set(gobbyResponses.filter(r => !r.themes?.length).map(r => r.Response_UID)).size, votes: unthemedCount}] : []).sort((a, b) => b.responses - a.responses);

                        // Adjust presetData for Mixed
                        if (displayType === 'Mixed' && gobbyResponses.length > 0) {
                            presetData.push({id: 'Free text responses', responses: gobbyResponses.length});
                        }

                        // Pre-sorted arrays (reused for chart variants below)
                        const themesByContributors = [...themesEngagement].sort((a, b) => Number(b.contributors) - Number(a.contributors));
                        const themesByResponses = [...themesEngagement].sort((a, b) => Number(b.responses) - Number(a.responses));
                        const themesByUnique = [...themesEngagement].sort((a, b) => Number(b.uniqueResponses) - Number(a.uniqueResponses));
                        const themesByTotalEngagement = [...themesEngagement].sort((a, b) =>
                            (Number(b.contributors) + Number(b.responses) + Number(b.uniqueResponses)) -
                            (Number(a.contributors) + Number(a.responses) + Number(a.uniqueResponses))
                        );
                        const themesByUniqueAndVotes = [...themesEngagement].sort((a, b) =>
                            (Number(b.uniqueResponses) + Number(b.votes)) -
                            (Number(a.uniqueResponses) + Number(a.votes))
                        );

                        return {
                            ...questionDetail,
                            contributors,
                            responses,
                            uniqueResponses,
                            votes,
                            moderated,
                            unthemed,
                            mostVoted,
                            questionData: question, // For charts
                            presetData,
                            presetDataForChart: [...presetData].sort((a, b) => b.responses - a.responses).slice(0, 10).reverse(),
                            themesEngagement,
                            themesByContributors,
                            themesByResponses,
                            themesByUnique,
                            themesByTotalEngagement,
                            themesByUniqueAndVotes,
                            // Top 10, reversed for charts (pre-computed to avoid recalculation per render)
                            themesByContributorsForChart: themesByContributors.slice(0, 10).reverse(),
                            themesByResponsesForChart: themesByResponses.slice(0, 10).reverse(),
                            themesByUniqueForChart: themesByUnique.slice(0, 10).reverse(),
                            themesByTotalEngagementForChart: themesByTotalEngagement.slice(0, 10).reverse(),
                            themesByUniqueAndVotesForChart: themesByUniqueAndVotes.slice(0, 10).reverse(),
                            unthemedCount,
                        };
                    });
                    perQuestionDataArray.sort((a, b) => a.number - b.number);
                    const perQuestionDataFiltered = perQuestionDataArray.filter(q => !q.pii);
                    setPerQuestionData(perQuestionDataFiltered);
                    if (!selectedQuestionId && perQuestionDataFiltered.length > 0) {
                        setSelectedQuestionId(perQuestionDataFiltered[0].number.toString());
                    }
                    setQuestionDetails(questionDetailsData);
                }
            }
        }
    }, [
        rawSurveyReport,
        survey?.end_date,
        survey?.state,
        surveyAbuse,
        surveyQuestions,
    ]);

    return {
        pieDemographicData,
        completionData,
        surveyTimePeriod,
        questionDetails,
        totalResponses,
        totalUnThemed,
        totalVotes,
        totalModeratedResponses,
        topThemesForSurvey,
        allResponsesByThemePerQ,
        topThemesForEachQuestion,
        questionEngagementData,
        themeEngagementData,
        perQuestionData,
        themeByContributors,
        themeByContributorsForChart,
        themeByResponses,
        themeByResponsesForChart,
        themeByUnique,
        themeByUniqueForChart,
        themeByTotalEngagement,
        themeByTotalEngagementForChart,
        questionByContributors,
        questionByContributorsForChart,
        questionByResponses,
        questionByResponsesForChart,
        questionByUnique,
        questionByUniqueForChart,
        questionByTotalEngagement,
        questionByTotalEngagementForChart,
    };
}
